import { detectNotation } from '../chords/chord'
import type { Notation } from '../chords/notes'
import type {
  ChordAnchor,
  Line,
  Occurrence,
  Provenance,
  RevisionContent,
  Section,
  SectionKind,
} from '../schema/v1'
import { normalizeForSearch } from '../search'
import { normalizeSource } from '../text'
import type { Issue } from '../validate'

/**
 * Parser propio del subconjunto de ChordPro que usa Atril (ADR 0001: ChordSheetJS es GPL).
 *
 * - Metadatos: title, artist, key, capo, tempo, time; el resto se guarda en `meta`.
 * - Secciones: start_of_X / end_of_X (y las abreviaturas sov, soc, sob, sot…).
 * - {chorus} repite el último estribillo en la secuencia.
 * - {comment} (c, ci, cb) es una línea de comentario.
 * - Líneas fuera de secciones: se agrupan en secciones implícitas separadas por líneas vacías.
 * - Acordes en línea: [G]texto. El anclaje es la posición en el texto NFC sin los corchetes.
 */
export interface ChordProResult {
  title?: string
  artist?: string
  content: RevisionContent
  issues: Issue[]
}

export interface ChordProOptions {
  /** Notación de los acordes. Por defecto se detecta. */
  notation?: Notation | 'auto'
  /**
   * Solo el cuerpo (secciones, letra, acordes y repeticiones): las directivas de metadatos se
   * ignoran con un aviso. Es lo que edita el editor de fichas, donde el tono, la cejilla y el
   * tempo tienen su propio formulario.
   */
  bodyOnly?: boolean
}

const SHORT_ENVIRONMENTS: Record<string, string> = {
  sov: 'start_of_verse',
  eov: 'end_of_verse',
  soc: 'start_of_chorus',
  eoc: 'end_of_chorus',
  sob: 'start_of_bridge',
  eob: 'end_of_bridge',
  sot: 'start_of_tab',
  eot: 'end_of_tab',
}

const KIND_BY_ENVIRONMENT: Record<string, SectionKind> = {
  intro: 'intro',
  verse: 'verse',
  prechorus: 'prechorus',
  pre_chorus: 'prechorus',
  chorus: 'chorus',
  bridge: 'bridge',
  instrumental: 'instrumental',
  solo: 'solo',
  interlude: 'interlude',
  outro: 'outro',
  tab: 'tab',
  part: 'other',
}

const COMMENT_DIRECTIVES = new Set(['comment', 'c', 'comment_italic', 'ci', 'comment_box', 'cb'])
/** `{repeat: Estrofa 1}` o `{repeat: #3}`: otra aparición de una sección ya escrita. */
const REPEAT_DIRECTIVES = new Set(['repeat', 'x_atril_repeat'])
const DIRECTIVE = /^\s*\{\s*([A-Za-z_][\w-]*)\s*(?::\s?(.*?))?\s*\}\s*$/

const IMPORT_PROVENANCE: Provenance = { kind: 'import', method: 'chordpro' }

export function parseChordPro(source: string, options: ChordProOptions = {}): ChordProResult {
  const issues: Issue[] = []
  const warn = (code: string, lineNumber: number, message: string) =>
    issues.push({ severity: 'warning', code, path: `line:${lineNumber}`, message })

  const counters = { s: 0, l: 0, c: 0, o: 0 }
  const nextId = (prefix: keyof typeof counters) => `${prefix}${++counters[prefix]}`

  const sections: Section[] = []
  const sequence: Occurrence[] = []
  const meta: Record<string, string> = {}
  const provenance: Record<string, Provenance> = {}
  const allChords: string[] = []
  let title: string | undefined
  let artist: string | undefined
  let capo = 0
  let shapeKey: string | undefined
  let timeSignature: string | undefined
  let bpm: number | undefined

  let current: { section: Section; explicit: boolean; environment?: string } | null = null
  let lastChorus: Section | null = null

  const openSection = (
    kind: SectionKind,
    explicit: boolean,
    label?: string,
    environment?: string,
  ) => {
    const section: Section = { id: nextId('s'), kind, lines: [] }
    if (label) section.label = label
    sections.push(section)
    sequence.push({ id: nextId('o'), section: section.id })
    current = { section, explicit }
    if (environment) current.environment = environment
    return section
  }

  const closeSection = () => {
    if (current?.section.kind === 'chorus') lastChorus = current.section
    current = null
  }

  const addLine = (line: Line) => {
    if (!current) openSection('other', false)
    current!.section.lines.push(line)
  }

  const rawLines = normalizeSource(source).split('\n')
  // Sin la línea vacía final que deja el último salto de línea.
  if (rawLines[rawLines.length - 1] === '') rawLines.pop()

  rawLines.forEach((raw, index) => {
    const lineNumber = index + 1
    const text = raw.replace(/\s+$/, '')
    const directive = DIRECTIVE.exec(text)

    if (directive) {
      const rawName = directive[1]!.toLowerCase()
      const name = SHORT_ENVIRONMENTS[rawName] ?? rawName
      const value = directive[2]?.trim() ?? ''

      if (name.startsWith('start_of_')) {
        const environment = name.slice('start_of_'.length)
        const kind = KIND_BY_ENVIRONMENT[environment]
        if (!kind)
          warn('chordpro.unknown-section', lineNumber, `Sección desconocida: ${environment}`)
        if (current) closeSection()
        openSection(kind ?? 'other', true, value || undefined, environment)
        return
      }
      if (name.startsWith('end_of_')) {
        if (!current || !current.explicit) {
          warn('chordpro.unmatched-end', lineNumber, `Cierre sin apertura: ${name}`)
        }
        closeSection()
        return
      }
      if (COMMENT_DIRECTIVES.has(name)) {
        addLine({ id: nextId('l'), kind: 'comment', text: value, chords: [] })
        return
      }
      if (REPEAT_DIRECTIVES.has(name)) {
        if (current && !current.explicit) closeSection()
        const target = findRepeatTarget(sections, value)
        if (!target) {
          warn('chordpro.repeat-unknown', lineNumber, `{repeat} no encuentra la sección: ${value}`)
          return
        }
        sequence.push({ id: nextId('o'), section: target.id })
        return
      }
      if (name === 'chorus') {
        if (current && !current.explicit) closeSection()
        const recalled = current?.section.kind === 'chorus' ? current.section : lastChorus
        if (!recalled) {
          warn('chordpro.chorus-without-chorus', lineNumber, '{chorus} sin estribillo previo')
          return
        }
        sequence.push({ id: nextId('o'), section: recalled.id })
        return
      }

      if (options.bodyOnly) {
        warn('chordpro.header-in-body', lineNumber, `{${rawName}} se edita en el formulario`)
        return
      }

      switch (name) {
        case 'title':
        case 't':
          title = value
          return
        case 'artist':
          artist = value
          return
        case 'key':
          shapeKey = value
          provenance.shapeKey = IMPORT_PROVENANCE
          return
        case 'capo': {
          const parsed = Number.parseInt(value, 10)
          if (Number.isInteger(parsed) && parsed >= 0 && parsed <= 12) capo = parsed
          else warn('chordpro.bad-capo', lineNumber, `Cejilla no válida: ${value}`)
          return
        }
        case 'tempo': {
          const parsed = Number.parseFloat(value)
          if (Number.isFinite(parsed) && parsed >= 20 && parsed <= 320) {
            bpm = parsed
            provenance['tempo.reference'] = IMPORT_PROVENANCE
          } else warn('chordpro.bad-tempo', lineNumber, `Tempo no válido: ${value}`)
          return
        }
        case 'time':
          if (/^([1-9]|1[0-6])\/(1|2|4|8|16)$/.test(value)) {
            timeSignature = value
            provenance.timeSignature = IMPORT_PROVENANCE
          } else warn('chordpro.bad-time', lineNumber, `Compás no válido: ${value}`)
          return
        default:
          meta[name] = value
          return
      }
    }

    if (text.startsWith('#')) {
      warn('chordpro.comment-dropped', lineNumber, 'Comentario de ChordPro ignorado')
      return
    }

    if (text.trim() === '') {
      if (current?.explicit) current.section.lines.push({ id: nextId('l'), text: '', chords: [] })
      else if (current) closeSection()
      return
    }

    const line = parseLyricLine(
      text,
      () => nextId('c'),
      (message) => warn('chordpro.bad-chord', lineNumber, message),
    )
    line.chords.forEach((anchor) => allChords.push(anchor.chord))
    addLine({ id: nextId('l'), ...line })
  })

  if (current && (current as { explicit: boolean }).explicit) {
    warn('chordpro.unclosed-section', rawLines.length, 'Sección sin cerrar al final del archivo')
  }

  const notation =
    options.notation && options.notation !== 'auto' ? options.notation : detectNotation(allChords)

  const content: RevisionContent = {
    notation,
    capo,
    tempo: bpm === undefined ? {} : { reference: { bpm } },
    sections,
    sequence,
    beatMap: { status: 'absent', events: [] },
    provenance,
  }
  if (shapeKey) content.shapeKey = shapeKey
  if (timeSignature) content.timeSignature = timeSignature
  if (Object.keys(meta).length > 0) content.meta = meta

  const result: ChordProResult = { content, issues }
  if (title !== undefined) result.title = title
  if (artist !== undefined) result.artist = artist
  return result
}

/** Resuelve la referencia de `{repeat: …}`: la etiqueta de una sección o «#n» (n-ésima escrita). */
function findRepeatTarget(sections: Section[], reference: string): Section | undefined {
  const ordinal = /^#(\d+)$/.exec(reference.trim())
  if (ordinal) return sections[Number(ordinal[1]) - 1]
  const wanted = normalizeForSearch(reference).replace(/\s+/g, ' ').trim()
  return sections.find(
    (section) =>
      section.label !== undefined &&
      normalizeForSearch(section.label).replace(/\s+/g, ' ').trim() === wanted,
  )
}

/** «[G]Una línea de [Em]ejemplo» → texto sin corchetes y anclajes por posición. */
export function parseLyricLine(
  source: string,
  nextChordId: () => string,
  onIssue: (message: string) => void = () => {},
): { text: string; chords: ChordAnchor[] } {
  let text = ''
  const chords: ChordAnchor[] = []
  let index = 0
  while (index < source.length) {
    const char = source[index]!
    if (char === '[') {
      const close = source.indexOf(']', index + 1)
      if (close < 0) {
        onIssue('Corchete sin cerrar; se deja como texto')
        text += source.slice(index)
        break
      }
      const chord = source.slice(index + 1, close).trim()
      if (chord === '') onIssue('Acorde vacío ignorado')
      else chords.push({ id: nextChordId(), at: text.length, chord })
      index = close + 1
      continue
    }
    text += char
    index += 1
  }
  return { text, chords }
}
